import SaveIcon from "@mui/icons-material/SaveAltRounded";
import { Button, Stack } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import FileIcon from "@entrilia/shared/components/FileIcon";
import FundraisingStatusTag from "@entrilia/shared/components/FundraisingStatusTag";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import CompletionPercentageIndicator from "../../../common/CompletionPercentageIndicator";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { formatDurationInSeconds } from "@entrilia/shared/utilities/formatters";
import adminApi from "../../../../api/adminApi";
import type { Category } from "../../../../api/types/accessTypes";
import type { DocumentFundInvestor, FundraisingDocument } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import CategoryTag from "../../../common/CategoryTag";
import { getCheckedGridSx } from "../../../common/grid/gridStyles";
import GridTextHoverAction from "@entrilia/shared/components/grid/GridTextHoverAction";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import { ActionsCell } from "./fundraisingDocumentsGridProvider";

const getColumnDefinitions = (
  excludeColumns: string[],
  categories: Category[],
  onDocumentPreview: (doc: FundraisingDocument) => void,
  onDocumentDownload: (doc: FundraisingDocument) => void,
  getFundraisingPath: (doc: FundraisingDocument) => string,
  onNavigateToDocumentActivity?: (doc: FundraisingDocument) => void
): GridColDef<FundraisingDocument>[] => {
  const columnDefs: GridColDef<FundraisingDocument>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      cellClassName: "highlighted-action",
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%" }}>
          <FileIcon fileExtension={getFileExtension(row.name)} />
          <GridTextHoverAction onClick={() => onDocumentPreview(row)}>{row.name}</GridTextHoverAction>
        </Stack>
      ),
    },
    {
      field: "fundInvestors.investorName",
      headerName: "Investor",
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) =>
        row.fundInvestors.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList<DocumentFundInvestor>
            displayCount={1}
            items={distinctBy(row.fundInvestors, (fi) => fi.investorId)}
            propGetter={(prop) => prop.investorName}
          />
        ),
    },
    {
      field: "fundInvestors.fundName",
      headerName: "Fund",
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) =>
        row.fundInvestors.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList<DocumentFundInvestor>
            displayCount={1}
            items={distinctBy(row.fundInvestors, (fi) => fi.fundId)}
            propGetter={(prop) => prop.fundName}
          />
        ),
    },
    {
      field: "categoryId",
      headerName: "Category",
      width: 250,
      renderCell: ({ row }) => <CategoryTag externalCategoryId={row.categoryId} />,
    },
    {
      field: "fundraisingName",
      headerName: "Fundraising",
      flex: 1,
      minWidth: 160,
      cellClassName: "highlighted-action",
      renderCell: ({ row }) => (
        <GridTextHoverLink to={getFundraisingPath(row)}>{row.fundraisingName}</GridTextHoverLink>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: ({ row }) => <FundraisingStatusTag status={row.status} />,
    },
    {
      field: "sentAt",
      headerName: "Publish Date",
      width: 120,
      valueFormatter: (value: string) => convertISODate(value),
    },
    {
      field: "views",
      headerName: "Views",
      type: "number",
      width: 100,
      valueGetter: (_, row) => row.views?.totalViews || null,
    },
    {
      field: "downloads",
      headerName: "Downloads",
      type: "number",
      width: 100,
      valueGetter: (_, row) => row.views?.totalDownloads || null,
    },
    {
      field: "totalViewTimeSeconds",
      headerName: "Total Time Spent",
      headerAlign: "right",
      align: "right",
      width: 140,
      valueGetter: (_, row) => {
        const totalViewTimeSeconds = row.views?.totalViewTimeSeconds;
        return totalViewTimeSeconds ? formatDurationInSeconds(totalViewTimeSeconds) : null;
      },
    },
    {
      field: "totalCompletionPercentage",
      headerName: "Total Completion",
      type: "number",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const completionPercentage = row.views?.totalCompletionPercentage;
        if (!completionPercentage) {
          return null;
        }

        return <CompletionPercentageIndicator value={completionPercentage} />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      cellClassName: "grid-row-actions",
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <ActionsCell
          row={row}
          onNavigateToDocumentActivity={onNavigateToDocumentActivity}
          onDownloadClick={onDocumentDownload}
        />
      ),
    },
  ];

  return columnDefs.filter((c) => !excludeColumns.includes(c.field));
};

interface Props {
  documents: FundraisingDocument[];
  categories: Category[];
  excludeColumns?: string[];
  isLoading?: boolean;
  onNavigateToDocumentActivity?: (document: FundraisingDocument) => void;
}

const getFundraisingFileDownloadInfo = withErrorHandling(adminApi.getFundraisingFileDownloadInfo);
const getDownloadUrlForMultipleFiles = withErrorHandling(adminApi.getDownloadUrlForMultipleFilesFromFundraisings);

const FundraisingDocumentsGrid = ({
  documents,
  categories,
  excludeColumns,
  isLoading,
  onNavigateToDocumentActivity,
}: Props) => {
  const { clientCode } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [documentPreviewState, setDocumentPreviewState] = useState<FileDownloadInfo>();

  const getFileDownloadInfo = useCallback(
    async (document: FundraisingDocument) => {
      const [downloadInfo, error] = await getFundraisingFileDownloadInfo(document.fundraisingId, document.id);
      if (error) {
        logError(error, "[FundraisingDocumentsGrid] downloadFileData");
        sendNotificationError(error.message);
        return undefined;
      }

      return downloadInfo;
    },
    [sendNotificationError]
  );

  const handleDownloadDocument = useCallback(
    async (document: FundraisingDocument) => {
      const fileInfo = await getFileDownloadInfo(document);
      if (fileInfo) {
        await downloadFileFromUrl(fileInfo.downloadUrl);
      }
    },
    [getFileDownloadInfo]
  );

  const handlePreviewDocument = useCallback(
    async (document: FundraisingDocument) => {
      const fileInfo = await getFileDownloadInfo(document);
      if (fileInfo) {
        setDocumentPreviewState(fileInfo);
      }
    },
    [getFileDownloadInfo]
  );

  const getFundraisingPath = useCallback(
    (document: FundraisingDocument) =>
      `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.fundraisings}/${document.fundraisingId}`,
    [clientCode]
  );

  const handleDownloadMultipleDocumentsClick = useCallback(async () => {
    const documentIdentifiers = documents
      .filter((d) => selectedIds.includes(d.id))
      .map((d) => ({ fundraisingId: d.fundraisingId, fundraisingDocumentId: d.id }));

    if (documentIdentifiers.length === 0) {
      return;
    }

    const [url, error] = await getDownloadUrlForMultipleFiles(documentIdentifiers, "fundraising_documents.zip");

    if (error) {
      logError(error, "[FundStructureDocumentsGrid] getDownloadUrlForSelectedDocuments");
      sendNotificationError(error.message);
      return;
    }

    await downloadFileFromUrl(url);
  }, [documents, selectedIds, sendNotificationError]);

  const columns = useMemo(
    () =>
      getColumnDefinitions(
        excludeColumns ?? [],
        categories,
        handlePreviewDocument,
        handleDownloadDocument,
        getFundraisingPath,
        onNavigateToDocumentActivity
      ),
    [
      categories,
      excludeColumns,
      handleDownloadDocument,
      getFundraisingPath,
      handlePreviewDocument,
      onNavigateToDocumentActivity,
    ]
  );

  const showToolbar = !!selectedIds.length;

  return (
    <Stack spacing={2.5} sx={{ height: "100%" }}>
      <DataGridHeaderToolbarProvider
        showToolbar={showToolbar}
        selection={[selectedIds, setSelectedIds]}
        actions={
          <Button
            disabled={selectedIds.length === 0 || isLoading}
            onClick={handleDownloadMultipleDocumentsClick}
            color="secondary"
            variant="text"
            startIcon={<SaveIcon />}
          >
            Download
          </Button>
        }
      >
        <DataGrid<FundraisingDocument>
          checkboxSelection
          rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
          onRowSelectionModelChange={(newSelection) => setSelectedIds(getSelectedRowIdsFromModel(newSelection))}
          sx={getCheckedGridSx}
          columns={columns}
          loading={isLoading}
          rows={documents}
          noRowsText="No documents"
          slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
          disableColumnReorder
          disableColumnSorting
        />
      </DataGridHeaderToolbarProvider>

      {documentPreviewState && (
        <PreviewFileDialog
          url={documentPreviewState.downloadUrl}
          fileName={documentPreviewState.fileName}
          onClose={() => setDocumentPreviewState(undefined)}
        />
      )}
    </Stack>
  );
};

export default FundraisingDocumentsGrid;
